import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { navColors, Sidebar } from './Sidebar';
import { useAuth } from '../auth/AuthContext';
import { NewYearPrompt } from './NewYearPrompt';
import { useLiveUpdates } from '../hooks/useLiveUpdates';

/**
 * Paints the whole page in the colour of the section you are in.
 *
 * Set as CSS variables on the document rather than passed down as props: the
 * shared classes in index.css read them, so a page picks up its colour just by
 * existing, and nothing has to be threaded through nine page components or
 * remembered by the tenth. Modals render in portals outside the layout, and
 * putting the variables on the root means they are coloured too.
 */
function useSectionAccent(themeId: string) {
  const { pathname } = useLocation();

  useEffect(() => {
    const colors = navColors();

    // Longest matching prefix, so /data/<id> is still Data.
    const key =
      Object.keys(colors)
        .filter((route) => route !== '/' && pathname.startsWith(route))
        .sort((a, b) => b.length - a.length)[0] ?? '/';

    const color = colors[key] ?? colors['/']!;
    const root = document.documentElement;

    root.style.setProperty('--accent-wash', color.wash);
    root.style.setProperty('--accent-tint', color.tint);
    root.style.setProperty('--accent-base', color.base);
    root.style.setProperty('--accent-label', color.label);
    root.style.setProperty('--accent-deep', color.deep);
    root.style.setProperty('--accent-press', color.press);
    root.style.setProperty('--accent-fill', color.fill);
    // The theme is a dependency too: the same route wears a different colour
    // once the palette changes.
  }, [pathname, themeId]);
}

export function AppLayout() {
  const { user } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  // One stream for the whole app, not one per page: it lives as long as the
  // layout does, so navigating around does not tear it down and rebuild it.
  useLiveUpdates(!!user);
  useSectionAccent(user?.settings.theme ?? 'boho');

  /*
   * The shell has no background of its own. It covers the whole viewport, so
   * painting it cream hid the body underneath — which is where the chosen
   * pattern and the paper grain are drawn. The body is already cream; letting
   * it show through is the entire point.
   */
  return (
    /*
     * `print:block` and `print:min-h-0` matter more than they look.
     *
     * A paper page is about 816px wide, which is above Tailwind's `md`
     * breakpoint, so every `md:` rule applies when printing. The shell was
     * therefore still a flex row one full viewport tall — and a viewport in
     * print is a sheet of paper. That empty row was the mysterious blank
     * first page on every printout in the app.
     */
    <div className="flex min-h-screen print:block print:min-h-0">
      {/* Renders nothing until a year has rolled over and the new dates are unconfirmed. */}
      <NewYearPrompt />

      {/*
        Sticky and exactly one screen tall, so the navigation is still there
        after scrolling to the bottom of a long roster. `self-start` matters:
        a flex child stretches to the row's height by default, which makes it
        as tall as the page and leaves nothing for `sticky` to do.
      */}
      {/*
        The rail is hidden on paper, wrapper and all. Hiding only the <nav>
        inside it left this div behind at `md:h-screen` — an empty column
        exactly one page tall.
      */}
      <div className="hidden self-start md:sticky md:top-0 md:block md:h-screen print:!hidden">
        <Sidebar />
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 flex md:hidden">
          <div
            className="absolute inset-0 bg-espresso/30"
            onClick={() => setMobileOpen(false)}
            aria-hidden="true"
          />
          <div className="relative z-10">
            <Sidebar onNavigate={() => setMobileOpen(false)} />
          </div>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        {/*
          Hidden on paper as well as on desktop. Print lays out against the
          page width, which is narrow enough to count as "mobile", so this bar
          was appearing at the top of every printout.
        */}
        <header className="flex items-center gap-3 border-b border-sand bg-linen px-4 py-3 md:hidden print:hidden">
          <button
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
            className="rounded-lg p-1.5 text-walnut hover:bg-sand/60"
          >
            <svg
              viewBox="0 0 24 24"
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
            >
              <path d="M4 6h16M4 12h16M4 18h16" strokeLinecap="round" />
            </svg>
          </button>
          <span className="font-display text-base">Teacher Dashboard</span>
        </header>

        <main className="min-w-0 flex-1 p-4 md:p-8 print:p-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
